"use client";
import PrimaryBtn from "@/components/PrimaryBtn";

import { Icons, Videos } from "@assets/assets";
import SectionHeader from "@/components/SectionHeader";
// import Spline from '@splinetool/react-spline'
import React, { useState, useEffect } from "react";
import { ExperienceItem } from "@/lib/Types";

export type expArrayItem = {
  value: string;
  text: string;
};

interface HeroContentProps {
  mainHeading: React.ReactNode | string;
  // The three descriptions are injected as raw HTML, so they must be strings.
  Desc1: string;
  Desc2: string;
  Desc3?: string;
  expArray?: expArrayItem[];
  buttonHref: string;
  buttonText?: string;
}

export default function HeroContent({
  mainHeading,
  Desc1,
  Desc2,
  Desc3,
  buttonText,
  expArray = [],
  buttonHref,
}: HeroContentProps) {
  const [mounted, setMounted] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [canLoadVideo, setCanLoadVideo] = useState(false);

  useEffect(() => {
    setMounted(true);
    const checkIsDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkIsDesktop();

    // Defer high-bandwidth video load to keep LCP fast
    const timer = setTimeout(() => {
      setCanLoadVideo(true);
    }, 2500);

    window.addEventListener("resize", checkIsDesktop);
    return () => {
      window.removeEventListener("resize", checkIsDesktop);
      clearTimeout(timer);
    };
  }, []);

  const hasExperience = expArray && expArray.length > 0;
  return (
    <section
      id="herocontent"
      className="relative bg-[#ECEEF0] flex flex-col items-center justify-center"
    >
      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] relative z-1">
        <SectionHeader
          title={mainHeading}
          subtitle="About us"
          paragraph=""
          button={
            <PrimaryBtn
              styleClass="ml-auto"
              dark={false}
              text={buttonText || "Schedule a call"}
              iconSrc={Icons?.chevron}
              href={`${process.env.NEXT_PUBLIC_BASE_URL}/${buttonHref}`}
            />
          }
          className="opacity-100 pt-[30px] lg:pt-[100px]"
          dark={false}
        />

        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-[20px] 2xl:gap-[100px]">
          <div className="spline-container w-full sm:w-[300px] md:w-[360px] lg:w-[400px] min-w-0 pt-[20px] lg:pt-[50px] hidden lg:block">
            {mounted && isDesktop && canLoadVideo && (
              <video
                autoPlay={false}
                loop
                muted
                preload="auto"
                playsInline
                className="web-toggle-video w-full"
              >
                <source src={Videos.modelVideo2} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>

          <div className="self-stretch inline-flex flex-col justify-start items-start gap-4 lg:gap-9  pt-[0px] md:pt-[60px] 2xl:pt-[75px] w-[100%]">
            {Desc1 ? (
              <div
                className="self-stretch justify-start text-[#00040b]/70 text-[14px] lg:text-[length:var(--hero-para)] font-normal leading-[150%]"
                dangerouslySetInnerHTML={{ __html: Desc1 }}
              />
            ) : null}

            {Desc2 ? (
              <div
                className="self-stretch justify-start text-[#00040b]/70 text-[14px] lg:text-[length:var(--hero-para)] font-normal leading-[150%]"
                dangerouslySetInnerHTML={{ __html: Desc2 }}
              />
            ) : null}

            {Desc3 ? (
              <div
                className="self-stretch justify-start text-[#00040b]/70 text-[14px] lg:text-[length:var(--hero-para)] font-normal leading-[150%]"
                dangerouslySetInnerHTML={{ __html: Desc3 }}
              />
            ) : null}
            {hasExperience && (
              <div
                className={`w-full px-2 sm:px-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-${expArray?.length} border border-[rgba(99,99,100,0.2)] divide-y sm:divide-y md:divide-y-0 md:divide-x divide-[rgba(99,99,100,0.2)]`}
              >
                {expArray &&
                  expArray?.map((item: ExperienceItem, idx: number) => {
                    return (
                      <div
                        key={`exp-${item?.value}-${item?.text}-${idx}`}
                        className="flex flex-col items-start justify-center px-4 sm:px-8 py-8 sm:py-12 lg:py-16 clash-font"
                      >
                        <h3 className="text-[length:var(--space-17-32)] font-bold text-gray-900">
                          {item?.value}
                        </h3>
                        <p className="text-gray-800 font-medium">
                          {item?.text}
                        </p>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
